export type ReportFileFormat = 'html' | 'xlsx';

export class ReportUnavailableError extends Error {}

export class ReportUnauthorizedError extends Error {}

export function reportFileUrl(
    reportId: string,
    format: ReportFileFormat,
    baseUrl: string = import.meta.env.REACT_APP_API_URL || ''
): string {
    return `${baseUrl}/api-cms/reports/agent/${encodeURIComponent(
        reportId
    )}?format=${format}`;
}

function messageOf(body: string): string | undefined {
    try {
        return (JSON.parse(body) as { message?: string }).message;
    } catch {
        return undefined;
    }
}

export async function fetchReportFile(
    reportId: string,
    format: ReportFileFormat,
    {
        headers,
        fetchImpl = fetch,
        baseUrl,
    }: {
        headers: Record<string, string>;
        fetchImpl?: typeof fetch;
        baseUrl?: string;
    }
): Promise<{ blob: Blob; filename: string | null }> {
    const response = await fetchImpl(reportFileUrl(reportId, format, baseUrl), {
        method: 'GET',
        headers,
    });

    if (response.status === 404) {
        throw new ReportUnavailableError(`Report ${reportId} not found`);
    }
    if (response.status === 401) {
        throw new ReportUnauthorizedError(messageOf(await response.text()));
    }
    if (!response.ok) {
        throw new Error(`Report file request failed: ${response.status}`);
    }

    const filenameMatch = response.headers
        .get('content-disposition')
        ?.match(/filename="([^"]+)"|filename=([^;]+)/);

    return {
        blob: await response.blob(),
        filename: filenameMatch ? filenameMatch[1] ?? filenameMatch[2] : null,
    };
}

const PRINT_ON_LOAD =
    "<script>addEventListener('load', () => print())</script>";
const PAGE_URL_LIFETIME_MS = 60_000;

export function showReportPage(
    target: Window,
    blob: Blob,
    { print }: { print: boolean }
): void {
    // The page prints itself: navigating the tab gives it a new Window, so a
    // listener set on `target` here would never fire.
    const page = print
        ? new Blob([blob, PRINT_ON_LOAD], { type: blob.type || 'text/html' })
        : blob;
    const url = URL.createObjectURL(page);
    target.location.href = url;
    // The tab reads the blob after this returns: revoking at once can blank it.
    setTimeout(() => URL.revokeObjectURL(url), PAGE_URL_LIFETIME_MS);
}

export function saveReportWorkbook(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Some browsers have not started reading the blob when `click()` returns:
    // revoking early leaves them with an empty file.
    setTimeout(() => {
        URL.revokeObjectURL(url);
        link.remove();
    }, 100);
}
